import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Loader2, LogOut, Plus, Pencil, Trash2, Star, Download, Mail, ShoppingBag, Music2, Upload, FileAudio, Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_PRODUCT = {
  id: '',
  title: '',
  title_es: '',
  description: '',
  description_es: '',
  price: 1.99,
  image_url: '',
  spotify_url: '',
  youtube_playlist_url: '',
  songs: [],
  year: new Date().getFullYear(),
  featured: false,
  item_type: 'single',
  is_new_release: false,
  has_physical: false,
  shipping_fee: 4.95,
};

function ProductForm({ initial, onSubmit, submitting }) {
  const [form, setForm] = useState(initial || EMPTY_PRODUCT);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          ...form,
          price: Number(form.price),
          year: Number(form.year),
          songs: typeof form.songs === 'string'
            ? form.songs.split(',').map((s) => s.trim()).filter(Boolean)
            : form.songs,
        });
      }}
      className="space-y-3"
      data-testid="product-form"
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 uppercase">ID (slug)</label>
          <Input value={form.id} onChange={(e) => update('id', e.target.value)} required disabled={!!initial} data-testid="form-id" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Type</label>
          <select
            className="w-full h-10 border rounded-md px-3"
            value={form.item_type}
            onChange={(e) => update('item_type', e.target.value)}
            data-testid="form-type"
          >
            <option value="single">Single</option>
            <option value="album">Album</option>
            <option value="collection">Collection</option>
            <option value="soundtrack">Soundtrack (Pista)</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 uppercase">Title (EN)</label>
          <Input value={form.title} onChange={(e) => update('title', e.target.value)} required data-testid="form-title" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Title (ES)</label>
          <Input value={form.title_es} onChange={(e) => update('title_es', e.target.value)} required data-testid="form-title-es" />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-500 uppercase">Description (EN)</label>
        <Textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={2} data-testid="form-desc" />
      </div>
      <div>
        <label className="text-xs text-gray-500 uppercase">Description (ES)</label>
        <Textarea value={form.description_es} onChange={(e) => update('description_es', e.target.value)} rows={2} data-testid="form-desc-es" />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-500 uppercase">Price ($)</label>
          <Input type="number" step="0.01" value={form.price} onChange={(e) => update('price', e.target.value)} required data-testid="form-price" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Year</label>
          <Input type="number" value={form.year} onChange={(e) => update('year', e.target.value)} required data-testid="form-year" />
        </div>
        <div className="flex items-center gap-4 pt-5">
          <label className="text-xs uppercase flex items-center gap-2">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => update('featured', e.target.checked)} data-testid="form-featured" />
            Featured
          </label>
          <label className="text-xs uppercase flex items-center gap-2">
            <input type="checkbox" checked={!!form.is_new_release} onChange={(e) => update('is_new_release', e.target.checked)} data-testid="form-newrelease" />
            New Release
          </label>
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-500 uppercase">Cover Image URL</label>
        <Input value={form.image_url} onChange={(e) => update('image_url', e.target.value)} required data-testid="form-image" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 uppercase">Spotify URL</label>
          <Input value={form.spotify_url || ''} onChange={(e) => update('spotify_url', e.target.value)} data-testid="form-spotify" />
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">YouTube URL</label>
          <Input value={form.youtube_playlist_url || ''} onChange={(e) => update('youtube_playlist_url', e.target.value)} data-testid="form-youtube" />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-500 uppercase">Track List (comma separated)</label>
        <Input
          value={Array.isArray(form.songs) ? form.songs.join(', ') : form.songs}
          onChange={(e) => update('songs', e.target.value)}
          data-testid="form-songs"
        />
      </div>

      <div className="grid grid-cols-3 gap-3 items-center pt-2 border-t">
        <label className="text-xs uppercase flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!form.has_physical}
            onChange={(e) => update('has_physical', e.target.checked)}
            data-testid="form-has-physical"
          />
          Physical version
        </label>
        <div>
          <label className="text-xs text-gray-500 uppercase">Shipping ($)</label>
          <Input
            type="number"
            step="0.01"
            value={form.shipping_fee ?? 4.95}
            onChange={(e) => update('shipping_fee', Number(e.target.value))}
            disabled={!form.has_physical}
            data-testid="form-shipping-fee"
          />
        </div>
      </div>

      <DialogFooter>
        <Button type="submit" disabled={submitting} className="bg-gold-500 hover:bg-gold-600 text-white" data-testid="form-save">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [txs, setTxs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [search, setSearch] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, s] = await Promise.all([
        axios.get(`${API}/albums`),
        axios.get(`${API}/admin/transactions`, { headers: authHeaders() }),
        axios.get(`${API}/admin/newsletter/subscribers`, { headers: authHeaders() }),
      ]);
      setProducts(p.data);
      setTxs(t.data);
      setSubs(s.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(
    () =>
      products.filter(
        (p) =>
          !search ||
          p.title.toLowerCase().includes(search.toLowerCase()) ||
          p.title_es.toLowerCase().includes(search.toLowerCase())
      ),
    [products, search]
  );

  const newRelease = products.find((p) => p.is_new_release);
  const paidTxs = txs.filter((t) => t.payment_status === 'paid');
  const revenue = paidTxs.reduce((sum, t) => sum + (t.amount || 0), 0);

  const handleCreate = async (data) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/albums`, data, { headers: authHeaders() });
      toast.success('Product created');
      setCreating(false);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (data) => {
    setSubmitting(true);
    try {
      const { id, ...patch } = data;
      await axios.put(`${API}/admin/albums/${id}`, patch, { headers: authHeaders() });
      toast.success('Saved');
      setEditing(null);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete "${id}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/albums/${id}`, { headers: authHeaders() });
      toast.success('Deleted');
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSetNewRelease = async (id) => {
    try {
      await axios.post(`${API}/admin/albums/${id}/set-new-release`, null, { headers: authHeaders() });
      toast.success('New release updated');
      await fetchAll();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const [uploadingId, setUploadingId] = useState(null);
  const handleUploadMusic = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/admin/albums/${id}/upload-music`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded "${file.name}"`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const handleRemoveMusic = async (id) => {
    if (!window.confirm('Remove the uploaded music file for this product?')) return;
    try {
      await axios.delete(`${API}/admin/albums/${id}/upload-music`, { headers: authHeaders() });
      toast.success('Music file removed');
      await fetchAll();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/export`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Export failed');
    }
  };

  const handleResendReceipt = async (sessionId, customerEmail) => {
    setResendingId(sessionId);
    try {
      const res = await axios.post(
        `${API}/admin/transactions/${sessionId}/resend-receipt`,
        { base_url: window.location.origin },
        { headers: authHeaders() }
      );
      if (res.data.ok) {
        toast.success(`Receipt resent to ${res.data.email || customerEmail || 'customer'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend receipt');
    } finally {
      setResendingId(null);
    }
  };

  const handleTestEmail = async () => {
    const trimmed = testEmailAddr.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Enter a valid email address');
      return;
    }
    setTestingEmail(true);
    try {
      const res = await axios.post(`${API}/admin/test-email`, { to: trimmed }, { headers: authHeaders() });
      toast.success(`Test email sent from ${res.data.sender} → ID: ${res.data.email_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test email failed — check server logs');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSyncStripe = async () => {
    setResendingId('__sync__');
    try {
      const res = await axios.post(
        `${API}/admin/transactions/sync-stripe`,
        { base_url: window.location.origin },
        { headers: authHeaders() }
      );
      const data = res?.data || {};
      const checked = data.checked ?? 0;
      const updated_to_paid = data.updated_to_paid ?? 0;
      const receipts_sent = data.receipts_sent ?? 0;
      const errors = data.errors ?? 0;
      toast.success(
        `Synced ${checked} orders — ${updated_to_paid} confirmed paid, ${receipts_sent} receipt${receipts_sent === 1 ? '' : 's'} sent${errors ? `, ${errors} error(s)` : ''}`
      );
      await fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Stripe sync failed — please try again';
      toast.error(msg);
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-24 pb-16" data-testid="admin-dashboard">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-lato text-xs uppercase tracking-[0.3em] text-gold-600 mb-1">
              Admin Dashboard
            </p>
            <h1 className="font-playfair text-4xl font-bold text-charcoal">
              Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}
            </h1>
          </div>
          <Button
            onClick={() => { logout(); navigate('/'); }}
            variant="outline"
            className="border-charcoal text-charcoal hover:bg-charcoal hover:text-white"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={<Music2 />} label="Products" value={products.length} />
          <StatCard icon={<ShoppingBag />} label="Paid Orders" value={paidTxs.length} />
          <StatCard icon={<Mail />} label="Subscribers" value={subs.length} />
          <StatCard icon={<Star />} label="Revenue (paid)" value={`$${revenue.toFixed(2)}`} />
        </div>

        {/* New release summary */}
        <Card className="mb-8 border-gold-300 bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <Star className="w-8 h-8 text-gold-500 fill-gold-500" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-gray-500">Current New Release</p>
              <p className="font-playfair text-xl font-bold text-charcoal">
                {newRelease ? newRelease.title : 'None selected'}
              </p>
              {newRelease && (
                <p className="font-lato text-sm text-gray-500">
                  {newRelease.year} · ${newRelease.price?.toFixed(2)} · {newRelease.item_type}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter</TabsTrigger>
          </TabsList>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <Input
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm bg-white"
                data-testid="product-search"
              />
              <div className="flex-1" />
              <Dialog open={creating} onOpenChange={setCreating}>
                <DialogTrigger asChild>
                  <Button className="bg-gold-500 hover:bg-gold-600 text-white" data-testid="new-product-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add a new product</DialogTitle>
                  </DialogHeader>
                  <ProductForm onSubmit={handleCreate} submitting={submitting} />
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="py-20 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gold-500" />
              </div>
            ) : (
              <div className="bg-white rounded-md border overflow-hidden" data-testid="products-table">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Cover</th>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Year</th>
                      <th className="px-4 py-3">Price</th>
                      <th className="px-4 py-3">Music File</th>
                      <th className="px-4 py-3">Flags</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`row-${p.id}`}>
                        <td className="px-4 py-2">
                          <img src={p.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                        </td>
                        <td className="px-4 py-2">
                          <div className="font-medium text-charcoal">{p.title}</div>
                          <div className="text-xs text-gray-500">{p.title_es}</div>
                        </td>
                        <td className="px-4 py-2 text-gray-600">{p.item_type}</td>
                        <td className="px-4 py-2 text-gray-600">{p.year}</td>
                        <td className="px-4 py-2 font-semibold text-gold-600">${p.price?.toFixed(2)}</td>
                        <td className="px-4 py-2">
                          {p.download_path ? (
                            <div className="flex items-center gap-2">
                              <FileAudio className="w-4 h-4 text-green-600 shrink-0" />
                              <span className="text-xs text-gray-700 max-w-[160px] truncate" title={p.download_filename}>
                                {p.download_filename || 'uploaded'}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveMusic(p.id)}
                                className="text-[10px] text-red-500 hover:underline"
                                data-testid={`remove-music-${p.id}`}
                              >
                                remove
                              </button>
                            </div>
                          ) : (
                            <label
                              className="inline-flex items-center gap-1 text-xs text-gold-600 hover:text-gold-700 cursor-pointer"
                              data-testid={`upload-music-${p.id}`}
                            >
                              {uploadingId === p.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Upload className="w-3 h-3" />
                              )}
                              <span>{uploadingId === p.id ? 'Uploading…' : 'Upload MP3/ZIP'}</span>
                              <input
                                type="file"
                                accept=".mp3,.zip,.wav,.m4a,audio/*,application/zip"
                                className="hidden"
                                disabled={uploadingId === p.id}
                                onChange={(e) => handleUploadMusic(p.id, e.target.files?.[0])}
                              />
                            </label>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {p.is_new_release && (
                            <span className="inline-block bg-gold-500 text-white text-[10px] uppercase px-2 py-0.5 rounded mr-1">New</span>
                          )}
                          {p.featured && (
                            <span className="inline-block bg-charcoal text-white text-[10px] uppercase px-2 py-0.5 rounded">Featured</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right space-x-1">
                          {!p.is_new_release && (
                            <Button size="sm" variant="outline" onClick={() => handleSetNewRelease(p.id)} data-testid={`set-newrelease-${p.id}`}>
                              <Star className="w-3 h-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid={`edit-${p.id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(p.id)} data-testid={`delete-${p.id}`}>
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit {editing?.title}</DialogTitle>
                </DialogHeader>
                {editing && (
                  <ProductForm initial={editing} onSubmit={handleUpdate} submitting={submitting} />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500">{txs.filter(t => t.payment_status === 'paid').length} paid · {txs.filter(t => t.payment_status !== 'paid').length} pending</p>
              <Button
                variant="outline"
                size="sm"
                type="button"
                className="border-gold-400 text-gold-700 hover:bg-gold-50"
                disabled={resendingId === '__sync__'}
                onClick={handleSyncStripe}
                data-testid="sync-stripe-btn"
              >
                {resendingId === '__sync__'
                  ? <><Loader2 className="w-3 h-3 animate-spin mr-2" />Syncing…</>
                  : <><Send className="w-3 h-3 mr-2" />Sync with Stripe &amp; Send Missing Receipts</>
                }
              </Button>
            </div>
            {/* Test email row */}
            <div className="flex items-center gap-2 mb-5 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <Mail className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="text-xs text-amber-700 font-medium shrink-0">Test email delivery:</span>
              <Input
                type="email"
                placeholder="your@email.com"
                value={testEmailAddr}
                onChange={e => setTestEmailAddr(e.target.value)}
                className="h-7 text-xs flex-1 max-w-xs"
                data-testid="test-email-input"
              />
              <Button
                size="sm"
                variant="outline"
                type="button"
                className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50"
                disabled={testingEmail}
                onClick={handleTestEmail}
                data-testid="test-email-btn"
              >
                {testingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Send Test'}
              </Button>
            </div>
            <div className="bg-white rounded-md border overflow-hidden" data-testid="orders-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Album</th>
                    <th className="px-4 py-3">Format</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No orders yet</td></tr>
                  )}
                  {txs.map((t) => (
                    <tr key={t.session_id} className="border-t">
                      <td className="px-4 py-2 text-gray-500 text-xs">
                        {t.created_at ? new Date(t.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2 text-charcoal">{t.album_title}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`text-[10px] uppercase px-2 py-0.5 rounded ${
                            t.variant === 'physical'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-sky-100 text-sky-700'
                          }`}
                        >
                          {t.variant || 'digital'}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-semibold text-gold-600">${t.amount?.toFixed(2)}</td>
                      <td className="px-4 py-2 text-gray-600 text-xs">{t.customer_email || '—'}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`text-[10px] uppercase px-2 py-0.5 rounded ${
                            t.payment_status === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {t.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {t.payment_status === 'paid' && t.customer_email && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 px-2 border-gold-300 text-gold-700 hover:bg-gold-50"
                            disabled={resendingId === t.session_id}
                            onClick={() => handleResendReceipt(t.session_id, t.customer_email)}
                            data-testid={`resend-${t.session_id}`}
                          >
                            {resendingId === t.session_id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <><Send className="w-3 h-3 mr-1" />Resend</>
                            }
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* NEWSLETTER */}
          <TabsContent value="newsletter" className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <p className="text-sm text-gray-500">{subs.length} total subscribers</p>
              <div className="flex-1" />
              <Button variant="outline" onClick={handleExport} data-testid="export-csv">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            </div>
            <div className="bg-white rounded-md border overflow-hidden" data-testid="subs-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No subscribers yet</td></tr>
                  )}
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t">
                      <td className="px-4 py-2 text-charcoal">{s.email}</td>
                      <td className="px-4 py-2 text-gray-600">{s.name || '—'}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {s.subscribed_at ? new Date(s.subscribed_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value }) => (
  <Card className="bg-white border-gray-200">
    <CardContent className="p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-gold-50 text-gold-500 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="text-xs uppercase text-gray-500 tracking-wider">{label}</p>
        <p className="font-playfair text-2xl font-bold text-charcoal">{value}</p>
      </div>
    </CardContent>
  </Card>
);
